"use client";

/**
 * Legacy PropertyShowcase data inventory, retained in this replacement:
 * - Property record: server GET {API}/property/get-property-by-name/:slug in the route page.
 * - Flats with categories: useQuery GET {NEXT_PUBLIC_API_URL}/property-meta-data/flats-with-categories/:propertyId.
 * - Price-list units: useQuery through fetchPriceList, GET {NEXT_PUBLIC_API_URL}/property-meta-data/export-pricelist/:propertyId.
 * - Nearby projects: Redux getRecommendedProperty, GET {API}/property/recommended-property?longitude=:lng&latitude=:lat&radius=20.
 * - Schools: property.schools populated by the existing OneMap workflow; MapComponent also matches SCHOOLS_WITH_COORDINATES and exposes a school toggle.
 * - Compare: legacy CompareProperty client block; intentionally omitted because it is not a light addition to this page.
 * - FAQ: generatePropertyFAQ rendered with FAQPage JSON-LD; the NRIC entry is filtered here.
 * - Agent details: AGENT from lib/seo, the Winnie headshot and the existing WhatsApp number.
 * - Gallery URLs: generateImageURL(name, folder, file), which uses formatProjectName and NEXT_PUBLIC_IMAGE_URL.
 * - Floor-plan matching: floorPlanImageFor from lib/v2/availability, the matcher shared with the price-list route.
 * - Visitor notification: ipapi.co/json plus sendNotificationOnTelegram, retained without an on-load enquiry modal.
 */

import dynamic from "next/dynamic";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, CalendarDays, GraduationCap, MapPin, Users } from "lucide-react";
import axios from "axios";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/lib/redux/store";
import { getRecommendedProperty, sendNotificationOnTelegram } from "@/lib/redux/slice/propertySlice";
import { cleanTiptapHtml } from "@/lib/cleanHtml";
import { cn, formatPropertyName, generateImageURL } from "@/lib/utils";
import { fetchPriceList, summarise, type Unit } from "@/lib/v2/availability";
import { formatPriceShort, formatPsf, sanePrice, statusMeta, tenureLabel } from "@/lib/v2/format";
import { selectNearby } from "@/lib/v2/nearby";
import { projectVideos } from "@/lib/v2/video";
import { AvailabilityBars } from "../AvailabilityBars";
import { Button } from "../Button";
import { Pill } from "../Pill";
import { PropertyCardV2 } from "../PropertyCardV2";
import { StickyEnquiryPanel } from "../StickyEnquiryPanel";
import { WhatsAppButton } from "../WhatsAppButton";
import { ProjectAgentCard } from "./ProjectAgentCard";
import { ProjectCta } from "./ProjectCta";
import { ProjectEnquiryDialog } from "./ProjectEnquiryDialog";
import { ProjectFAQ } from "./ProjectFAQ";
import { ProjectFloorPlans } from "./ProjectFloorPlans";
import { ProjectGallery } from "./ProjectGallery";
import { ProjectPhotoStrip } from "./ProjectPhotoStrip";
import { ProjectVideos } from "./ProjectVideos";
import { ProjectCompare } from "./ProjectCompare";
import { ProjectPlanGrid } from "./ProjectPlanGrid";
import type { NearbyProject, ProjectProperty } from "./types";
import { trackMetaEvent } from "@/lib/metaPixel";
import { hasStackmap } from "@/lib/v2/stackmap";
import { ProjectStackMapSection } from "../stackmap/ProjectStackMapSection";

const ProjectMap = dynamic(
  () => import("./ProjectMap").then((module) => module.ProjectMap),
  {
    ssr: false,
    loading: () => <div className="h-[480px] animate-pulse rounded-2xl border border-line bg-surface md:h-[560px]" />,
  },
);

type UnitWithUpdatedAt = Unit & { updatedAt?: string | null };
type FlatCategoryResponse = { data?: { minPrice?: number | null } };

function ProjectSection({
  title,
  id,
  first,
  children,
}: {
  title: string;
  id?: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`scroll-mt-24 ${first ? "pb-10 md:pb-14" : "border-t border-line py-10 md:py-14"}`}>
      <h2 className="mb-6 font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
        {title}
      </h2>
      {children}
    </section>
  );
}

function validMoney(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function displayDate(value: unknown) {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Singapore",
  });
}

function splitChineseTitle(value: string) {
  const match = value.match(/^(.*?)(\s*[（(][^）)]*[\u3400-\u9fff][^）)]*[）)])\s*$/u);
  if (match) return { name: match[1].trim(), chinese: match[2].trim() };
  const firstCjk = value.search(/[\u3400-\u9fff]/u);
  if (firstCjk > 0) return { name: value.slice(0, firstCjk).trim(), chinese: value.slice(firstCjk).trim() };
  return { name: value, chinese: "" };
}

// Descriptions pasted from agent decks carry their own h1/h2 headings and
// emoji; keep them as bold sub-heads so the page outline stays ours.
function demoteHeadings(html: string): string {
  return html
    .replace(/<h[12]([^>]*)>/gi, '<h3$1 class="mt-6 text-base font-semibold text-ink">')
    .replace(/<\/h[12]>/gi, "</h3>")
    .replace(/(<h3[^>]*>)\s*(?:[\p{Extended_Pictographic}\uFE0F\u200D]+\s*)+/gu, "$1");
}

// The curated school list arrives as raw HTML, so the graduation-cap chip
// that sits before every school name elsewhere on the page is inlined here.
const SCHOOL_CHIP_HTML =
  '<span class="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-avail-soft text-avail" aria-hidden="true">' +
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>' +
  "</svg></span>";

function decorateSchoolList(html: string): string {
  return html
    .replace(/<li\b[^>]*>/gi, `<li class="flex items-start gap-2 py-0.5">${SCHOOL_CHIP_HTML}<span>`)
    .replace(/<\/li>/gi, "</span></li>");
}


export default function ProjectPage({
  property,
  districtLabel,
  initialNearby = [],
}: {
  property: ProjectProperty;
  districtLabel?: string | null;
  initialNearby?: NearbyProject[];
}) {
  const dispatch = useDispatch<AppDispatch>();
  const pathname = usePathname();
  const [nearby, setNearby] = useState<NearbyProject[]>(initialNearby);
  const [nearbyLoading, setNearbyLoading] = useState(true);
  // Held in a ref so the default `[]` prop never re-triggers the nearby effect.
  const initialNearbyRef = useRef(initialNearby);
  const ctaRowRef = useRef<HTMLDivElement>(null);
  const [ctaRowGone, setCtaRowGone] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [unitInterest, setUnitInterest] = useState<{ plan?: string; type?: string } | null>(null);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const status = statusMeta(property.construction_status, property.isPreLaunch);
  const soldOut = status.tone === "soldout";
  const preLaunch = Boolean(property.isPreLaunch);
  const slug = formatPropertyName(property.name);
  const propertyTitle = property.title?.trim() || property.name;
  const title = splitChineseTitle(propertyTitle);
  const tenure = tenureLabel(property.is_leasehold, property.lease_year);

  const { data: units = [], isLoading: unitsLoading } = useQuery({
    queryKey: ["v2-project-price-list", property._id],
    queryFn: () => fetchPriceList(property._id),
    enabled: Boolean(property._id),
  });
  const { data: flatCategories = [] } = useQuery<FlatCategoryResponse[]>({
    queryKey: ["property-meta-data", property._id],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_API_URL}/property-meta-data/flats-with-categories/${property._id}`,
        );
        return Array.isArray(response.data)
          ? (response.data as FlatCategoryResponse[])
          : [];
      } catch {
        return [];
      }
    },
    enabled: Boolean(property._id),
  });
  const summary = useMemo(() => summarise(units), [units]);
  const availableUnits = useMemo(
    () => units.filter((unit) => unit.status.trim().toLowerCase() === "available"),
    [units],
  );
  const availableSummary = useMemo(() => summarise(availableUnits), [availableUnits]);
  const fallbackCategoryPrice = flatCategories
    .map((item) => item.data?.minPrice)
    .find(validMoney);
  const priceFrom = validMoney(availableSummary.minPrice)
    ? availableSummary.minPrice
    : sanePrice(property.min_flat_price) ?? sanePrice(fallbackCategoryPrice);
  const lastUpdated = (units as UnitWithUpdatedAt[])
    .map((unit) => unit.updatedAt)
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  useEffect(() => {
    let cancelled = false;
    // The radius endpoint returns full records (coordinates, price, units),
    // so it tops up the server list, then both run through the same
    // district-first, open-for-sale filter.
    dispatch(getRecommendedProperty(property)).then((action) => {
      if (cancelled) return;
      const payload = (action as { payload?: unknown }).payload;
      if (Array.isArray(payload)) {
        const fetched = payload as NearbyProject[];
        const fetchedIds = new Set(fetched.map((item) => item._id));
        setNearby(selectNearby(property, [...fetched, ...initialNearbyRef.current.filter((item) => !fetchedIds.has(item._id))]));
      }
      setNearbyLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, property]);

  useEffect(() => {
    const row = ctaRowRef.current;
    if (!row || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      // Only once the row has scrolled up behind the 80px site header, never while it sits below the fold.
      ([entry]) => setCtaRowGone(!entry.isIntersecting && entry.boundingClientRect.bottom <= 80),
      { rootMargin: "-80px 0px 0px 0px" },
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const notify = async () => {
      try {
        const response = await axios.get("https://ipapi.co/json");
        const message = `IP ${response.data.ip} is accessing project ${property.name} ${process.env.NEXT_PUBLIC_FRONTEND_URL}${pathname} from ${response.data.country_name}, via ${navigator.userAgent}`;
        await dispatch(sendNotificationOnTelegram({ message }));
      } catch {
        // Analytics must never block project content.
      }
    };
    void notify();
  }, [dispatch, pathname, property.name]);

  // Meta ViewContent, once per project, so ads can reach people who looked
  // at this project.
  useEffect(() => {
    trackMetaEvent("ViewContent", {
      content_name: property.name,
      content_ids: [property._id],
      content_type: "product",
    });
  }, [property._id, property.name]);

  // "Get the price list" goes straight to the price list page (Sean, 15/09/2026);
  // the unlock form lives there. Pre-launch keeps the enquiry modal: no list yet.
  const priceListHref = `/single-property/${slug}/price-list`;
  const openEnquiry = (interest?: { plan?: string; type?: string }) => {
    setUnitInterest(interest ?? null);
    setEnquiryOpen(true);
  };

  const facts = ([
    ["Name", propertyTitle],
    // Shows the stored property_type as-is (Sean's call, 30/09/2026). The
    // derived kind still drives the FAQ set, so an EC page answers EC
    // questions even while this row reads "Condo".
    ["Type", property.property_type],
    ["Developer", property.project_developer],
    ["Address", property.full_address],
    ["Postal code", property.pin_code],
    ["District", districtLabel || property.district],
    ["Tenure", tenure],
    ["Expected TOP", displayDate(property.expected_top)],
    ["Total units", property.units ? property.units.toLocaleString("en-SG") : ""],
    ["Site area", property.site_area ? String(property.site_area) : ""],
    ["Car park lots", property.car_places_counting ? property.car_places_counting.toLocaleString("en-SG") : ""],
    ["Launch date", displayDate(property.launch_date)],
  ] as Array<[string, React.ReactNode]>).filter(
    ([, value]) => value != null && value !== "",
  );

  /**
   * Before a launch, a missing field is itself the answer. Dropping the row
   * let the page imply a date or price it does not have (and let stale
   * template defaults read as fact), so say "Not announced" out loud.
   * Only for pre-launch projects: on a selling or completed project a blank
   * TOP is a data gap, not news.
   */
  if (preLaunch) {
    const has = (label: string) => facts.some(([key]) => key === label);
    if (!has("Launch date")) facts.push(["Launch date", "Not announced"]);
    if (!has("Expected TOP")) facts.push(["Expected TOP", "Not announced"]);
    if (!validMoney(property.min_flat_price)) facts.push(["Price", "Not released"]);
  }
  const sitePlans = property.images?.site_plan ?? [];
  const unitMix = property.images?.unit_mix_breakdown ?? [];
  const videos = projectVideos(property);
  const schools = property.schools ?? [];
  const hasMapLocation =
    Number.isFinite(Number(property.location?.latitude)) &&
    Number.isFinite(Number(property.location?.longitude));

  return (
    <div className="pb-24 md:pb-0">
      <div className="mx-auto max-w-site px-4 pb-8 pt-5 md:px-8 md:pb-12 md:pt-8">
        <header className="mb-8 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <div className="flex flex-wrap gap-2">
              <Pill tone={status.tone}>{status.label}</Pill>
              {districtLabel && <Pill tone="neutral">{districtLabel}</Pill>}
              <Pill tone="neutral">{tenure}</Pill>
            </div>
            <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-ink md:text-5xl">
              {title.name}
              {title.chinese && <span className="ml-3 block text-2xl font-normal text-ink-faint sm:inline md:text-3xl">{title.chinese}</span>}
            </h1>
            {(preLaunch || property.full_address?.trim()) && (
              <p className="mt-3 flex items-start gap-2 text-ink-muted">
                <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                {preLaunch
                  ? `Pre-launch · Postal ${property.pin_code || "TBA"}`
                  : property.full_address}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 text-sm text-ink-muted">
              {property.project_developer && <span className="flex items-center gap-2"><Building2 aria-hidden="true" className="size-4" />{property.project_developer}</span>}
              {property.expected_top && <span className="flex items-center gap-2"><CalendarDays aria-hidden="true" className="size-4" />TOP {displayDate(property.expected_top)}</span>}
              {property.units > 0 && <span className="flex items-center gap-2"><Users aria-hidden="true" className="size-4" />{property.units.toLocaleString("en-SG")} units</span>}
            </div>
          </div>
          <div ref={ctaRowRef} className="flex flex-col gap-3 sm:flex-row lg:justify-end">
            {soldOut ? (
              <>
                <Button variant="secondary" size="lg" className="min-h-[52px] text-base" href="#nearby">See nearby launches</Button>
                <Button size="lg" className="min-h-[52px] text-base" onClick={() => openEnquiry()}>Get alerts for similar launches</Button>
              </>
            ) : (
              <>
                {preLaunch ? (
                  <Button size="lg" className="min-h-[52px] text-base" onClick={() => openEnquiry()}>Register for the price list</Button>
                ) : (
                  <Button size="lg" className="min-h-[52px] text-base" href={priceListHref}>Get the price list</Button>
                )}
                <Button variant="secondary" size="lg" className="min-h-[52px] text-base" href="#floor-plans">Floor plans</Button>
              </>
            )}
          </div>
        </header>

        {!soldOut && (
          <div
            aria-hidden={!ctaRowGone}
            className={cn(
              "fixed inset-x-0 top-20 z-30 hidden border-b border-line bg-white/95 shadow-[0_8px_30px_rgba(23,29,46,0.08)] backdrop-blur transition-all duration-200 md:block",
              ctaRowGone ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0",
            )}
          >
            <div className="mx-auto flex max-w-site items-center justify-between gap-6 px-4 py-3 md:px-8">
              <div className="min-w-0">
                <p className="truncate font-display text-lg font-semibold text-ink">{title.name}</p>
                <p className={`text-sm font-semibold ${validMoney(priceFrom) ? "text-gold-600" : "text-ink-faint"}`}>
                  {validMoney(priceFrom) ? `From ${formatPriceShort(priceFrom)}` : "Price on request"}
                </p>
              </div>
              {preLaunch ? (
                <Button size="lg" className="min-h-[52px] shrink-0 bg-ink text-base text-white hover:bg-ink/90" onClick={() => openEnquiry()}>
                  Register for the price list
                </Button>
              ) : (
                <Button size="lg" className="min-h-[52px] shrink-0 bg-ink text-base text-white hover:bg-ink/90" href={priceListHref}>
                  Get the price list
                </Button>
              )}
            </div>
          </div>
        )}

        <ProjectGallery property={property} />

        <div className="mt-10 grid gap-10 md:grid-cols-12 lg:gap-12">
          <main className="md:col-span-8">
            {facts.length > 0 && (
              <ProjectSection title="Key facts" first>
                <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {facts.map(([label, value]) => (
                    <div key={label} className="rounded-2xl border border-line bg-surface p-4">
                      <dt className="text-xs font-medium text-ink-faint">{label}</dt>
                      <dd className="mt-1.5 text-sm font-semibold text-ink">{value}</dd>
                    </div>
                  ))}
                </dl>
              </ProjectSection>
            )}

            <ProjectSection title={soldOut ? "Sales status" : preLaunch || summary.total === 0 ? "Prices" : "Units still available"} id="price-list-summary">
              {unitsLoading ? (
                <div className="space-y-4">
                  <div className="h-24 animate-pulse rounded-2xl bg-surface" />
                  <div className="h-60 animate-pulse rounded-2xl bg-surface" />
                </div>
              ) : soldOut ? (
                <div className="rounded-2xl border border-sold/20 bg-sold-soft p-6">
                  <p className="font-display text-xl font-semibold text-sold">This project is fully sold</p>
                  <Link href="#nearby" className="mt-3 inline-flex text-sm font-semibold text-ink underline decoration-line underline-offset-4">
                    See nearby launches
                  </Link>
                </div>
              ) : summary.total === 0 ? (
                <div className="rounded-2xl border border-line bg-surface p-6">
                  <p className="max-w-2xl leading-7 text-ink">
                    Prices are not released yet. Register and Winnie sends the price list on launch day.
                  </p>
                  <Button className="mt-5" onClick={() => openEnquiry()}>Register for the price list</Button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid gap-4 rounded-2xl bg-ink p-5 text-white sm:grid-cols-2 lg:grid-cols-4">
                    <div><p className="text-xs text-white/60">Available</p><p className="mt-1 font-display text-lg font-semibold">{summary.available} of {summary.total} units</p></div>
                    {validMoney(priceFrom) && <div className="border-white/15 sm:border-l sm:pl-4"><p className="text-xs text-white/60">From</p><p className="mt-1 font-display text-lg font-semibold text-gold-500">{formatPriceShort(priceFrom)}</p></div>}
                    {validMoney(availableSummary.minPsf) && <div className="border-white/15 lg:border-l lg:pl-4"><p className="text-xs text-white/60">Price per sqft</p><p className="mt-1 font-display text-lg font-semibold">{formatPsf(availableSummary.minPsf)}</p></div>}
                    {lastUpdated && <div className="border-white/15 sm:border-l sm:pl-4"><p className="text-xs text-white/60">Last updated</p><p className="mt-1 text-sm font-semibold">{displayDate(lastUpdated)}</p></div>}
                  </div>
                  <AvailabilityBars rows={summary.byType.map((row) => ({ label: row.type, total: row.total, available: row.available, priceFrom: row.minPrice }))} />
                  <Button href={`/single-property/${slug}/price-list`}>See the full price list</Button>
                </div>
              )}
            </ProjectSection>

          </main>

          {/* The column stretches to the row height so the sticky wrapper can travel with the left column and stop at its end. */}
          <div id="enquiry-panel" className="md:col-span-4 md:self-stretch">
            <div className="md:sticky md:top-24">
              <StickyEnquiryPanel
                propertyId={property._id}
                propertyName={propertyTitle}
                priceFrom={validMoney(priceFrom) ? priceFrom : null}
                status={status.label}
                soldOut={soldOut}
                submitLabel={preLaunch ? "Register for the price list" : undefined}
              />
            </div>
          </div>
        </div>

        <div className="mt-2">
            {hasStackmap(slug) && (
              <ProjectSection title="See every home in 3D" id="view-3d">
                <ProjectStackMapSection slug={slug} propertyId={property._id} propertyName={property.name} />
              </ProjectSection>
            )}

            {(summary.byPlan.length > 0 || (property.images?.floor_images?.length ?? 0) > 0) && (
              <ProjectSection title="Floor plans" id="floor-plans">
                <ProjectFloorPlans
                  projectName={property.name}
                  floorImages={property.images?.floor_images ?? []}
                  plans={soldOut ? [] : summary.byPlan}
                  onAsk={(interest) => openEnquiry(interest)}
                />
                {!soldOut && (
                  <ProjectCta
                    heading="Want the price for a layout?"
                    body="Tell Winnie which layout you like and she sends the unit prices and the floor plan PDF."
                    action={
                      preLaunch ? (
                        <Button size="lg" className="text-base" onClick={() => openEnquiry()}>Register for the price list</Button>
                      ) : (
                        <Button size="lg" className="text-base" href={priceListHref}>Get the price list</Button>
                      )
                    }
                  />
                )}
              </ProjectSection>
            )}

            {sitePlans.length > 0 && <ProjectSection title="Site plan">{<ProjectPlanGrid property={property} folder="site_plan" files={sitePlans} />}</ProjectSection>}
            {unitMix.length > 0 && <ProjectSection title="Unit mix">{<ProjectPlanGrid property={property} folder="unit_mix_breakdown" files={unitMix} />}</ProjectSection>}

            {(hasMapLocation || schools.length > 0) && (
              <ProjectSection title="Where it sits">
                {hasMapLocation && <ProjectMap property={property} nearby={nearby} />}
                {property.school_description?.trim() && (
                  <div className="mt-6 rounded-2xl border border-line bg-white p-5">
                    <h3 className="font-display text-lg font-semibold text-ink">Schools in the area</h3>
                    <div
                      className="mt-3 text-sm leading-7 text-ink-muted md:columns-3 md:gap-8 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-ink [&_li]:my-0 [&_ul]:list-none [&_ul]:pl-0"
                      dangerouslySetInnerHTML={{ __html: decorateSchoolList(demoteHeadings(cleanTiptapHtml(property.school_description))) }}
                    />
                  </div>
                )}
                {schools.length > 0 && (
                  <div className={`${hasMapLocation ? "mt-6" : ""} grid gap-x-8 gap-y-3 sm:grid-cols-2`}>
                    {schools.map((school) => (
                      <div key={school.name} className="flex items-start justify-between gap-4 rounded-xl bg-surface px-4 py-3 text-sm">
                        <span className="flex min-w-0 items-start gap-2 font-medium text-ink">
                          <span className="mt-px flex size-6 shrink-0 items-center justify-center rounded-full bg-avail-soft text-avail">
                            <GraduationCap aria-hidden="true" className="size-3.5" />
                          </span>
                          {school.name}
                        </span>
                        {school.distance_km != null && <span className="shrink-0 text-ink-muted">{school.distance_km.toFixed(2)} km</span>}
                      </div>
                    ))}
                  </div>
                )}
              </ProjectSection>
            )}

            {property.description && (
              <ProjectSection title="About the project">
                <div className={`text-base leading-7 text-ink-muted [&_a]:text-brand-600 [&_a]:underline [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-4 [&_ul]:list-disc [&_ul]:pl-5 ${descriptionOpen ? "" : "line-clamp-8"}`} dangerouslySetInnerHTML={{ __html: demoteHeadings(cleanTiptapHtml(property.description)) }} />
                <button type="button" onClick={() => setDescriptionOpen((open) => !open)} className="mt-4 text-sm font-semibold text-brand-700 underline decoration-brand-100 underline-offset-4">
                  {descriptionOpen ? "Read less" : "Read more"}
                </button>
              </ProjectSection>
            )}

            {(property.images?.property_images?.length ?? 0) > 1 && (
              <ProjectSection title="Photos" id="photos">
                <ProjectPhotoStrip property={property} />
              </ProjectSection>
            )}

            {videos.length > 0 && (
              <ProjectSection title="Videos" id="videos">
                <ProjectVideos property={property} />
              </ProjectSection>
            )}

            {(nearbyLoading || nearby.length > 0) && (
              <ProjectSection title="Nearby launches" id="nearby">
                {nearbyLoading && nearby.length === 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="aspect-[4/5] animate-pulse rounded-2xl bg-surface" />)}</div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {nearby.map((item) => {
                      const nearbyImage = item.images?.property_images?.[0]
                        ? generateImageURL(item.name, "property_images", item.images.property_images[0])
                        : null;
                      return (
                        <PropertyCardV2
                          key={item._id}
                          slug={formatPropertyName(item.name)}
                          name={item.title || item.name}
                          district={item.district || ""}
                          address={item.full_address}
                          image={nearbyImage}
                          constructionStatus={item.construction_status || "Upcoming"}
                          isPreLaunch={item.isPreLaunch}
                          priceFrom={validMoney(item.min_flat_price) ? item.min_flat_price : null}
                          psfFrom={validMoney(item.psf_from) ? item.psf_from : null}
                          unitsTotal={item.units}
                          expectedTop={item.expected_top}
                          tenure={tenureLabel(Boolean(item.is_leasehold), item.lease_year)}
                          href={`/single-property/${formatPropertyName(item.name)}`}
                          distance={item.distance_km ?? item.distance}
                        />
                      );
                    })}
                  </div>
                )}
              </ProjectSection>
            )}

            {nearby.length > 0 && (
              <ProjectSection title="Compare with nearby launches" id="compare">
                <ProjectCompare property={property} nearby={nearby} currentUnits={units} />
                {!soldOut && (
                  <ProjectCta
                    heading="Book a showflat visit"
                    body="Winnie takes you through this project and the launches you compared, on one visit."
                    action={
                      <WhatsAppButton
                        size="lg"
                        label="WhatsApp Winnie"
                        text={`Hi Winnie, I would like to book a showflat visit for ${propertyTitle} on AI Property`}
                      />
                    }
                  />
                )}
              </ProjectSection>
            )}

            <ProjectSection title={`Questions about ${title.name}`}>
              <ProjectFAQ property={property} displayName={title.name} soldOut={soldOut} />
            </ProjectSection>

            <ProjectSection title="Your agent">
              <ProjectAgentCard projectName={propertyTitle} />
            </ProjectSection>
        </div>
      </div>

      <ProjectEnquiryDialog
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        propertyId={property._id}
        propertyName={propertyTitle}
        interest={unitInterest ?? undefined}
      />
    </div>
  );
}
